import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpErrorResponse } from '@angular/common/http';

import { AuthService } from '../../../core/auth/auth.service';

type ResendState = 'idle' | 'sending' | 'sent' | 'already_verified' | 'error';

@Component({
  selector: 'app-check-email',
  imports: [
    RouterLink,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './check-email.html',
  styleUrl: './check-email.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckEmailComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);

  protected email = signal<string>('');
  protected resendState = signal<ResendState>('idle');
  protected countdown = signal<number | null>(null);

  protected readonly emailForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
  });

  protected get emailControl() {
    return this.emailForm.controls.email;
  }

  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    // Email is only sourced from navigation state (in-memory). It is intentionally NOT
    // read from a URL query param: emails are PII and would leak into server access logs,
    // browser history, Referer headers, proxies, and analytics. When state is empty
    // (direct landing or page refresh), the template renders an inline email-input form.
    const stateEmail = (history.state as { email?: string }).email;
    if (stateEmail) this.email.set(stateEmail);
    this.destroyRef.onDestroy(() => this.clearCountdown());
  }

  protected onResend(): void {
    const email = this.email();
    if (!email || this.resendState() === 'sending' || this.countdown() !== null) return;
    this.sendVerification(email);
  }

  protected onSubmitEmail(): void {
    this.emailForm.markAllAsTouched();
    if (this.emailForm.invalid || this.resendState() === 'sending' || this.countdown() !== null) {
      return;
    }
    const email = this.emailControl.value.trim();
    if (!email) return;

    this.sendVerification(email, /* setEmailOnSuccess */ true);
  }

  private sendVerification(email: string, setEmailOnSuccess = false): void {
    this.resendState.set('sending');
    this.authService.resendVerification(email).subscribe({
      next: () => {
        if (setEmailOnSuccess) this.email.set(email);
        this.resendState.set('sent');
      },
      error: (err: unknown) => {
        if (err instanceof HttpErrorResponse) {
          const authErr = this.authService.getAuthError(err);
          if (authErr?.code === 'ALREADY_VERIFIED') {
            this.resendState.set('already_verified');
            return;
          }
          if (authErr?.code === 'RESEND_RATE_LIMITED' && authErr.retry_after) {
            this.resendState.set('idle');
            this.startCountdown(authErr.retry_after);
            return;
          }
        }
        this.resendState.set('error');
      },
    });
  }

  private startCountdown(seconds: number): void {
    this.clearCountdown();
    this.countdown.set(seconds);
    this.countdownTimer = setInterval(() => {
      const remaining = (this.countdown() ?? 1) - 1;
      if (remaining <= 0) {
        this.clearCountdown();
      } else {
        this.countdown.set(remaining);
      }
    }, 1000);
  }

  private clearCountdown(): void {
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    this.countdown.set(null);
  }
}
